import { reportStoreError } from '@/store/reportStoreError'
import { fetchSyncPreview, runSync, fetchSyncStatus } from '@/services/syncApi'

const POLL_INTERVAL_MS = 1500

function pollTask(taskId, onProgress) {
  return new Promise((resolve, reject) => {
    const timer = setInterval(async () => {
      try {
        const data = await fetchSyncStatus(taskId)
        if (data.status === 'PROGRESS') {
          onProgress(data.meta)
        } else if (data.status === 'SUCCESS') {
          clearInterval(timer)
          resolve(data.result)
        } else if (data.status === 'FAILURE') {
          clearInterval(timer)
          reject(new Error(data.error || 'Task failed'))
        }
      } catch (e) {
        clearInterval(timer)
        reject(e)
      }
    }, POLL_INTERVAL_MS)
  })
}

export const sync = {
  namespaced: true,
  state: () => ({
    preview: null,
    loading: false,
    running: false,
    log: [],
  }),
  mutations: {
    SET_PREVIEW(state, data) { state.preview = data },
    SET_LOADING(state, val) { state.loading = val },
    SET_RUNNING(state, val) { state.running = val },
    ADD_LOG(state, event) { state.log.push(event) },
    CLEAR_LOG(state) { state.log = [] },
  },
  actions: {
    async fetchPreview({ commit }, mappingIds = null) {
      commit('SET_LOADING', true)
      try {
        const { task_id } = await fetchSyncPreview(mappingIds)
        const diffs = await pollTask(task_id, () => {})
        commit('SET_PREVIEW', diffs)
      } catch (e) {
        reportStoreError('sync/fetchPreview', e)
        commit('chat/SHOW_NOTIFICATION', { type: 'error', message: `Erreur prévisualisation : ${e.message}` }, { root: true })
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async runSync({ commit }, mappingIds = null) {
      commit('SET_RUNNING', true)
      commit('CLEAR_LOG')
      const seenMappings = new Set()
      try {
        const { task_id } = await runSync(mappingIds)
        const result = await pollTask(task_id, (meta) => {
          if (meta && meta.mapping && !seenMappings.has(meta.mapping)) {
            seenMappings.add(meta.mapping)
            commit('ADD_LOG', { type: 'mapping_start', mapping: meta.mapping })
          }
        })
        for (const m of result.results) {
          if (m.error) {
            commit('ADD_LOG', { type: 'error', mapping: m.mapping_name, message: m.error })
            continue
          }
          for (const file of m.uploaded) {
            commit('ADD_LOG', { type: 'upload_done', mapping: m.mapping_name, file })
          }
          for (const err of m.upload_errors) {
            commit('ADD_LOG', { type: 'upload_error', mapping: m.mapping_name, file: err.file, message: err.message })
          }
          for (const file of m.removed) {
            commit('ADD_LOG', { type: 'remove_done', mapping: m.mapping_name, file })
          }
          for (const err of m.remove_errors) {
            commit('ADD_LOG', { type: 'remove_error', mapping: m.mapping_name, file: err.file, message: err.message })
          }
          commit('ADD_LOG', { type: 'mapping_done', mapping: m.mapping_name })
        }
        commit('ADD_LOG', { type: 'all_done' })
      } catch (e) {
        reportStoreError('sync/runSync', e)
        commit('chat/SHOW_NOTIFICATION', { type: 'error', message: `Erreur synchronisation : ${e.message}` }, { root: true })
        commit('ADD_LOG', { type: 'error', mapping: '', message: e.message })
      } finally {
        commit('SET_RUNNING', false)
      }
    },
  },
  getters: {
    hasPendingChanges: state => {
      if (!state.preview) return false
      return state.preview.some(m => m.to_add.length > 0 || m.to_remove.length > 0)
    },
  },
}
